<!-- src/views/FileViewer.vue -->
<template>
  <div class="popup" :style="popupStyle" ref="popup">
    <v-toolbar dark color="#6a1b9a" @mousedown="startDrag">
      <v-toolbar-title> {{ fileName }}</v-toolbar-title>

      <!-- Reset Zoom -->
      <v-btn
        v-if="
          (previewType === 'pdf' && pdfZoom !== 100) ||
          (previewType === 'docx' && docxZoom !== 1) ||
          (previewType === 'pptx' && pptxZoom !== 1)
        "
        icon
        color="black"
        dark
        variant="elevated"
        style="margin-left: 8px"
        @click="resetZoom"
      >
        <v-icon>mdi-refresh</v-icon>
      </v-btn>

      <!-- Zoom In -->
      <v-btn
        icon
        color="black"
        dark
        variant="elevated"
        style="margin-left: 8px"
        @click="zoomIn"
      >
        <v-icon>mdi-magnify-plus</v-icon>
      </v-btn>

      <!-- Zoom Out -->
      <v-btn
        icon
        color="black"
        dark
        variant="elevated"
        style="margin-left: 8px"
        @click="zoomOut"
      >
        <v-icon>mdi-magnify-minus</v-icon>
      </v-btn>

      <!-- Download -->
      <v-btn
        icon
        color="black"
        dark
        variant="elevated"
        style="margin-left: 8px"
        @click="$emit('download')"
      >
        <v-icon>mdi-download</v-icon>
      </v-btn>

      <!-- Maximize/Minimize -->
      <v-btn
        icon
        color="black"
        dark
        variant="elevated"
        style="margin-left: 8px"
        @click="toggleMaximize"
      >
        <v-icon>{{
          isMaximized ? "mdi-window-minimize" : "mdi-window-maximize"
        }}</v-icon>
      </v-btn>

      <!-- Exit -->
      <v-btn
        icon
        color="black"
        dark
        variant="elevated"
        style="margin-left: 8px"
        @click="$emit('close')"
      >
        <v-icon>mdi-close</v-icon>
      </v-btn>
    </v-toolbar>

    <div class="preview-container">
      <div v-if="loading" class="loading-overlay">
        <v-progress-circular
          indeterminate
          size="48"
          width="4"
          color="primary"
          class="spinner"
        ></v-progress-circular>
        <span class="loading-text">Loading {{ fileName }}</span>
        <!-- Additional notice for PPTX or large files -->
        <span class="loading-text" v-if="previewType === 'pptx'">
          PPTX previews can take longer, especially for larger files.
        </span>
        <span class="loading-text" v-else>
          Loading may take longer for larger files.
        </span>
      </div>

      <!-- PDF preview -->
      <PDF
        v-if="previewType === 'pdf'"
        :src="previewPdfPath"
        :key="previewPdfPath + pdfZoom"
        :pdfWidth="pdfWidth"
        class="preview-item"
      />

      <!-- DOCX preview -->
      <div
        v-if="previewType === 'docx'"
        class="docx-wrapper preview-item"
        :style="docxStyle"
      >
        <div ref="docxContainer" class="docx-preview-content"></div>
      </div>

      <!-- PPTX preview -->
      <div
        v-if="previewType === 'pptx'"
        ref="pptxContainer"
        :style="pptxStyle"
        class="pptx-wrapper preview-item"
      ></div>
    </div>

    <!-- Resize handle -->
    <div v-show="!isMaximized" class="resize-handle" @mousedown="startResize">
      <v-icon class="reversed">mdi-arrow-expand</v-icon>
    </div>
  </div>
</template>

<script>
import PDF from "pdf-vue3";
import { init } from "pptx-preview";
import * as docx from "docx-preview";
import { VProgressCircular } from "vuetify/lib/components";

export default {
  name: "FileViewer",
  components: { PDF, VProgressCircular },
  props: {
    previewKey: { type: String, required: true },
    previewType: { type: String, required: true },
    previewPdfPath: { type: String, default: "" },
    previewDocxBuffer: { type: [ArrayBuffer, null], default: null },
    previewPptxBuffer: { type: [ArrayBuffer, null], default: null },
  },
  data() {
    return {
      isMaximized: false,
      originalPosition: { top: "25%", left: "25%" },
      originalSize: { width: "50%", height: "50%" },
      popupPosition: { top: "5px", left: "50%" },
      popupSize: { width: "50%", height: "99%" },

      dragging: false,
      resizing: false,
      dragStart: { x: 0, y: 0, top: 0, left: 0 },
      resizeStart: { x: 0, y: 0, width: 0, height: 0 },

      pdfZoom: 100,
      docxZoom: 1.0,
      pptxZoom: 1.0,

      loading: true,

      pptxPreviewer: null,
    };
  },
  computed: {
    fileName() {
      const parts = this.previewKey.split("/");
      return parts[parts.length - 1];
    },
    popupStyle() {
      return {
        position: "fixed",
        top: this.popupPosition.top,
        left: this.popupPosition.left,
        width: this.popupSize.width,
        height: this.popupSize.height,
        border: "2px solid black",
        boxShadow: "0 4px 8px rgba(0,0,0,0.2)",
        zIndex: 1000,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
      };
    },
    pdfWidth() {
      return this.pdfZoom + "%";
    },
    docxStyle() {
      return {
        transform: `scale(${this.docxZoom})`,
        transformOrigin: "top center",
      };
    },
    pptxStyle() {
      return {
        transform: `scale(${this.pptxZoom})`,
        transformOrigin: "top center",
      };
    },
  },
  watch: {
    previewKey() {
      this.loading = true;
      this.pptxPreviewer = null;

      // Clear all preview DOM content
      if (this.$refs.docxContainer) {
        this.$refs.docxContainer.innerHTML = "";
      }
      if (this.$refs.pptxContainer) {
        this.$refs.pptxContainer.innerHTML = "";
      }

      // Force PDF re-render by resetting pdfZoom
      if (this.previewType === "pdf") {
        this.pdfZoom = 100; // Optional: reset zoom for a clean render
      }
    },

    previewDocxBuffer(buffer) {
      if (this.previewType === "docx" && buffer && this.$refs.docxContainer) {
        docx.renderAsync(
          buffer,
          this.$refs.docxContainer,
          this.$refs.docxContainer,
          { className: "docx", breakPages: true, ignoreWidth: true }
        );
        this.loading = false;
      }
    },
    previewPptxBuffer(buffer) {
      if (this.previewType === "pptx" && buffer && this.$refs.pptxContainer) {
        // initialize or reuse
        this.pptxPreviewer = init(this.$refs.pptxContainer, {
          width: this.$refs.pptxContainer.clientWidth,
          height: this.$refs.pptxContainer.clientHeight,
        });
        this.pptxPreviewer.preview(buffer);
        this.loading = false;
      }
    },
  },
  methods: {
    resetZoom() {
      this.pdfZoom = 100;
      this.docxZoom = 1.0;
      this.pptxZoom = 1.0;
    },
    zoomIn() {
      if (this.previewType === "pdf") {
        this.pdfZoom = Math.min(this.pdfZoom + 20, 500);
      } else if (this.previewType === "docx") {
        this.docxZoom = Math.min(this.docxZoom + 0.2, 3);
      } else if (this.previewType === "pptx") {
        this.pptxZoom = Math.min(this.pptxZoom + 0.2, 3);
      }
    },
    zoomOut() {
      if (this.previewType === "pdf") {
        this.pdfZoom = Math.max(this.pdfZoom - 20, 20);
      } else if (this.previewType === "docx") {
        this.docxZoom = Math.max(this.docxZoom - 0.2, 0.2);
      } else if (this.previewType === "pptx") {
        this.pptxZoom = Math.min(this.pptxZoom - 0.2, 3);
      }
    },
    toggleMaximize() {
      if (this.isMaximized) {
        this.popupPosition = { ...this.originalPosition };
        this.popupSize = { ...this.originalSize };
      } else {
        this.originalPosition = { ...this.popupPosition };
        this.originalSize = { ...this.popupSize };
        this.popupPosition = { top: "0", left: "0" };
        this.popupSize = { width: "100%", height: "100%" };
      }
      this.isMaximized = !this.isMaximized;
    },
    startDrag(event) {
      if (this.isMaximized) return;
      const rect = this.$refs.popup.getBoundingClientRect();
      this.dragging = true;
      this.dragStart = {
        x: event.clientX,
        y: event.clientY,
        top: rect.top,
        left: rect.left,
      };
      document.addEventListener("mousemove", this.onDrag);
      document.addEventListener("mouseup", this.stopDrag);
    },
    onDrag(event) {
      if (!this.dragging) return;
      const dx = event.clientX - this.dragStart.x;
      const dy = event.clientY - this.dragStart.y;
      const rawLeft = this.dragStart.left + dx;
      const rawTop = this.dragStart.top + dy;
      const popupEl = this.$refs.popup;
      const pw = popupEl.offsetWidth;
      const ph = popupEl.offsetHeight;
      const maxLeft = window.innerWidth - pw;
      const maxTop = window.innerHeight - ph;
      const clampedLeft = Math.min(Math.max(0, rawLeft), maxLeft);
      const clampedTop = Math.min(Math.max(0, rawTop), maxTop);
      this.popupPosition = {
        left: `${clampedLeft}px`,
        top: `${clampedTop}px`,
      };
    },
    stopDrag() {
      this.dragging = false;
      document.removeEventListener("mousemove", this.onDrag);
      document.removeEventListener("mouseup", this.stopDrag);
    },
    startResize(event) {
      if (this.isMaximized) return;
      const rect = this.$refs.popup.getBoundingClientRect();
      this.resizing = true;
      this.resizeStart = {
        x: event.clientX,
        y: event.clientY,
        width: rect.width,
        height: rect.height,
      };
      document.addEventListener("mousemove", this.onResize);
      document.addEventListener("mouseup", this.stopResize);
    },
    onResize(event) {
      if (!this.resizing) return;
      const dx = event.clientX - this.resizeStart.x;
      const dy = event.clientY - this.resizeStart.y;
      this.popupSize = {
        width: `${this.resizeStart.width + dx}px`,
        height: `${this.resizeStart.height + dy}px`,
      };
    },
    stopResize() {
      this.resizing = false;
      document.removeEventListener("mousemove", this.onResize);
      document.removeEventListener("mouseup", this.stopResize);
    },
  },
};
</script>

<style scoped>
.popup {
  border-radius: 8px;
  position: relative;
}

.preview-container {
  flex: 1;
  overflow: auto;
  background: white;
  position: relative;
}

.preview-item {
  background: white;
  z-index: 20;
}

.docx-preview-content .docx {
  width: 100% !important;
  box-sizing: border-box;
}

.pptx-wrapper .slide {
  width: 100% !important;
  box-sizing: border-box;
}

.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: white;
  z-index: 10;
}

.loading-overlay .spinner {
  margin-bottom: 12px;
}

.loading-overlay .loading-text {
  font-size: 1.1em;
  font-weight: 500;
  color: #333;
}

.resize-handle {
  position: absolute;
  bottom: 0px;
  right: 0px;
  width: 24px;
  height: 24px;
  cursor: nwse-resize;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: rotate(90deg);
}
</style>
